import { cn } from "../lib/cn";

/** The ink loaded in the stamp pad: indigo approval, green go-ahead, red refusal. */
export type StampTone = "accent" | "working" | "stopped";

const toneClasses: Record<StampTone, string> = {
  accent: "border-accent-deep text-accent-deep",
  working: "border-ink-working text-ink-working",
  stopped: "border-ink-stopped text-ink-stopped",
};

export interface StampProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Stamp ink. Defaults to the indigo `accent` pad. */
  tone?: StampTone;
  /** Play the thunk on mount. */
  animateIn?: boolean;
  children: React.ReactNode;
  ref?: React.Ref<HTMLSpanElement>;
}

/**
 * A rubber stamp: slightly under-inked, applied by a person on purpose. Use it
 * for sign-offs, approvals, and acknowledgements - the mark a human left on a
 * record, never a computed status (that is a highlighter Swipe).
 *
 * It lands square. The under-inking is what says rubber stamp; a tilt would only
 * make the word harder to read.
 */
export function Stamp({
  tone = "accent",
  animateIn = false,
  className,
  children,
  ref,
  ...rest
}: StampProps) {
  return (
    <span
      ref={ref}
      className={cn(
        "inline-block border-2 bg-transparent px-2 py-0.5 rounded-[2px]",
        "font-mono uppercase text-[0.6875rem] font-medium tracking-[0.13em]",
        // Under-inked edges: the print thins out away from the middle.
        "[mask-image:radial-gradient(ellipse_at_center,black_60%,rgba(0,0,0,0.72)_100%)]",
        toneClasses[tone],
        animateIn &&
          "[animation:bds-stamp_260ms_var(--ease-paper)_both] motion-reduce:animate-none",
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}
